import ReconnectingWebSocket from 'reconnecting-websocket';

import { proxyAxios } from 'api/apiBase/axios';
import { IOrderbook, ITickerSocket, TradeSocket } from 'types/cex/spot';
import { IChartTimeframe, KLine } from 'types/charts';
import { normalizeNumber } from 'utils/formats/normalizeNumber';

import { BaseStrategy, IInitArgs } from '../base_strategy';
import { aggregateOrderbook } from '../utils';

/**
 * CoinW market-data strategy for the CURRENT (CexCharts) trading widget.
 *
 * CoinW exposes a Poloniex-style public REST API
 * (`/api/v1/public?command=...`, UNDERSCORE symbols) plus a token-gated
 * socket.io / Engine.IO v3 WebSocket dialled from the endpoint returned by
 * `/pusher/public-token` (HYPHEN symbols). All REST goes through the dashboard
 * proxy (`/api/v1/proxy`) to avoid CORS / Cloudflare, exactly like KuCoin.
 *
 * Ground truth: 111-connman/connector/coinw.
 *
 * Notes:
 *  - There is NO public REST trade-history command (`returnTradeHistory`
 *    returns code -3), so `fetchTrades` returns []. Live trades arrive over the
 *    `spot/market-api-trade:` WS channel.
 *  - There is NO kline WS channel, so live candles are aggregated from the
 *    public trades stream into the active interval bucket.
 */

const COINW_REST_BASE = 'https://api.coinw.com';
const COINW_PUBLIC = `${COINW_REST_BASE}/api/v1/public`;
const COINW_PUBLIC_TOKEN = `${COINW_REST_BASE}/pusher/public-token`;

const DEFAULT_PING_INTERVAL_MS = 10_000;
const MIN_PING_INTERVAL_MS = 5_000;

// Channel prefixes (with trailing colon) — mirror connman common/enum.go.
const DEPTH_CHANNEL = 'spot/level2:';
const TICKER_CHANNEL = 'spot/market-api-ticker:';
const TRADE_CHANNEL = 'spot/market-api-trade:';

// IChartTimeframe -> returnChartData `period` (seconds). CoinW supports the
// Poloniex-style discrete buckets; unknown timeframes fall back to 5m.
const KLINE_PERIOD_SECONDS: Partial<Record<IChartTimeframe, number>> = {
  '1m': 60,
  '3m': 180,
  '5m': 300,
  '15m': 900,
  '30m': 1800,
  '1h': 3600,
  '2h': 7200,
  '4h': 14400,
  '1d': 86400,
  '1w': 604800,
};

interface CoinwPublicToken {
  token: string;
  endpoint: string;
  protocol?: string;
  pingInterval?: number;
}

interface CoinwDepthData {
  asks?: string[][];
  bids?: string[][];
}

interface CoinwTickerData {
  last?: string;
  high?: string;
  low?: string;
  vol?: string;
  volValue?: string;
  changePrice?: string;
  changeRate?: string;
}

interface CoinwTradeData {
  price?: string;
  size?: string;
  side?: string;
  time?: string | number;
}

const clampPingInterval = (value: number | undefined): number => {
  if (!value || !Number.isFinite(value)) {
    return DEFAULT_PING_INTERVAL_MS;
  }
  return Math.max(value, MIN_PING_INTERVAL_MS);
};

/** Normalises a CoinW timestamp (seconds or ms) to ms. */
const toMs = (value: string | number | undefined): number => {
  const numeric = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(numeric)) {
    return Date.now();
  }
  return numeric < 1_000_000_000_000 ? numeric * 1000 : numeric;
};

/** Decodes the double-JSON-encoded `data` field carried inside a socket.io event envelope. */
const parseEnvelopeData = <T>(envelope: { data?: unknown }): T | null => {
  const raw = envelope?.data;
  if (raw === undefined || raw === null) {
    return null;
  }
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw) as T;
    } catch {
      return null;
    }
  }
  if (typeof raw === 'object') {
    return raw as T;
  }
  return null;
};

export class CoinwStrategy implements BaseStrategy {
  ws: ReconnectingWebSocket | undefined;
  pingInterval: ReturnType<typeof setInterval> | undefined;

  /** REST (underscore) symbol, e.g. `XNK_USDT`. Satisfies BaseStrategy.pair. */
  pair: string;
  /** WS (hyphen) symbol, e.g. `XNK-USDT`. */
  wsPair: string;
  base: string;
  quote: string;

  bids: Map<string, string>;
  asks: Map<string, string>;
  snapshotReady: boolean;

  depth: number;
  precision: number;
  selectedPrecision: number;

  // Trades -> kline bucket aggregation (no kline WS channel on CoinW).
  klineBucketStart: number | null;
  klineOpen: number;
  klineHigh: number;
  klineLow: number;
  klineClose: number;
  klineVolume: number;
  intervalMs: number;

  onOrderbookUpdate: (v: IOrderbook) => void;
  onTickerUpdate: (v: ITickerSocket) => void;
  klineUpdate: (v: KLine) => void;
  precisionUpdate: (v: number) => void;
  onTradeUpdate: (v: TradeSocket) => void;

  constructor({
    base,
    quote,
    orderbookDepth,
    onOrderbookUpdate,
    onTickerUpdate,
    klineUpdate,
    precisionUpdate,
    onTradeUpdate,
  }: IInitArgs) {
    this.base = base.toUpperCase();
    this.quote = quote.toUpperCase();
    this.pair = `${this.base}_${this.quote}`;
    this.wsPair = `${this.base}-${this.quote}`;
    this.bids = new Map();
    this.asks = new Map();
    this.snapshotReady = false;
    this.depth = orderbookDepth ?? 20;
    this.precision = 1;
    this.selectedPrecision = 1;
    this.klineBucketStart = null;
    this.klineOpen = 0;
    this.klineHigh = 0;
    this.klineLow = 0;
    this.klineClose = 0;
    this.klineVolume = 0;
    this.intervalMs = 300_000;
    this.onOrderbookUpdate = onOrderbookUpdate;
    this.onTickerUpdate = onTickerUpdate;
    this.klineUpdate = klineUpdate;
    this.precisionUpdate = precisionUpdate;
    this.onTradeUpdate = onTradeUpdate;
  }

  setOrderbookDepth(depth: number) {
    this.depth = depth;
  }

  private buildPublicUrl = (command: string, extra: Record<string, string> = {}): string => {
    const url = new URL(COINW_PUBLIC);
    url.searchParams.set('command', command);
    Object.entries(extra).forEach(([key, value]) => url.searchParams.set(key, value));
    return url.toString();
  };

  fetchPrecision = async () => {
    try {
      const res = await proxyAxios.get<{
        data?: { currencyPair: string; pricePrecision: number }[];
      }>('/api/v1/proxy', {
        params: { url: this.buildPublicUrl('returnSymbol') },
      });

      const found = (res.data?.data ?? []).find(el => el.currencyPair === this.pair);

      if (found && Number.isFinite(found.pricePrecision)) {
        this.precision = found.pricePrecision;
        this.selectedPrecision = found.pricePrecision;
        this.precisionUpdate(found.pricePrecision);
        return;
      }
    } catch (error) {
      console.log('CoinW precision (returnSymbol) error: ', error);
    }

    // Fallback: derive precision from the orderbook snapshot's top ask price.
    try {
      const res = await proxyAxios.get<{
        data?: { asks: string[][]; bids: string[][] };
      }>('/api/v1/proxy', {
        params: { url: this.buildPublicUrl('returnOrderBook', { symbol: this.pair, size: '20' }) },
      });

      const topAsk = res.data?.data?.asks?.[0]?.[0];
      const prec = topAsk && topAsk.includes('.') ? topAsk.split('.')[1].length : 2;
      this.precision = prec;
      this.selectedPrecision = prec;
      this.precisionUpdate(prec);
    } catch (error) {
      console.log('CoinW precision (fallback) error: ', error);
      this.precisionUpdate(this.precision);
    }
  };

  private emitOrderbook = () => {
    const asks = [...this.asks.entries()].map<[string, string]>(([price, qty]) => [price, qty]);
    const bids = [...this.bids.entries()].map<[string, string]>(([price, qty]) => [price, qty]);

    this.onOrderbookUpdate(aggregateOrderbook(asks, bids, this.selectedPrecision, this.depth));
  };

  private applyLevels = (levels: string[][] | undefined, side: Map<string, string>) => {
    if (!levels) {
      return;
    }
    for (const level of levels) {
      const price = level?.[0];
      const qty = level?.[1];
      if (price === undefined) {
        continue;
      }
      if (qty === undefined || parseFloat(qty) === 0) {
        side.delete(price);
      } else {
        side.set(price, qty);
      }
    }
  };

  fetchOrderbook = async () => {
    try {
      const res = await proxyAxios.get<{
        data?: { asks: string[][]; bids: string[][] };
      }>('/api/v1/proxy', {
        params: {
          url: this.buildPublicUrl('returnOrderBook', { symbol: this.pair, size: '50' }),
        },
      });

      const data = res.data?.data;
      if (!data) {
        return;
      }

      this.asks.clear();
      this.bids.clear();
      (data.asks ?? []).forEach(([price, qty]) => {
        if (price !== undefined && qty !== undefined) this.asks.set(price, qty);
      });
      (data.bids ?? []).forEach(([price, qty]) => {
        if (price !== undefined && qty !== undefined) this.bids.set(price, qty);
      });

      this.snapshotReady = true;
      this.emitOrderbook();
    } catch (error) {
      console.log('CoinW orderbook error: ', error);
    }
  };

  fetchKlines = async ({
    base,
    quote,
    from,
    to,
    interval,
  }: {
    base: string;
    quote: string;
    from: number;
    to: number;
    limit: number;
    interval: IChartTimeframe;
  }) => {
    try {
      const period = KLINE_PERIOD_SECONDS[interval] ?? 300;

      const res = await proxyAxios.get<{
        data?: { date: number; open: string; high: string; low: string; close: string; volume: string }[];
      }>('/api/v1/proxy', {
        params: {
          url: this.buildPublicUrl('returnChartData', {
            currencyPair: this.pair,
            period: String(period),
            start: String(Math.floor(from / 1000)),
            end: String(Math.floor(to / 1000)),
          }),
        },
      });

      return (res.data?.data ?? []).map(
        el =>
          [toMs(el.date), el.open, el.high, el.low, el.close, el.volume] as unknown as KLine,
      );
    } catch (error) {
      console.log('CoinW klines error: ', error);
      return [];
    }
  };

  // CoinW has no public REST trade-history endpoint; live trades arrive via WS.
  fetchTrades = async () => {
    return [];
  };

  private aggregateTradeIntoKline = (trade: CoinwTradeData) => {
    const price = Number(trade.price);
    if (!Number.isFinite(price)) {
      return;
    }
    const qty = Number(trade.size);
    const safeQty = Number.isFinite(qty) ? qty : 0;
    const ts = toMs(trade.time);
    const bucket = Math.floor(ts / this.intervalMs) * this.intervalMs;

    if (this.klineBucketStart === null || bucket > this.klineBucketStart) {
      this.klineBucketStart = bucket;
      this.klineOpen = price;
      this.klineHigh = price;
      this.klineLow = price;
      this.klineClose = price;
      this.klineVolume = safeQty;
    } else {
      this.klineClose = price;
      if (price > this.klineHigh) this.klineHigh = price;
      if (price < this.klineLow) this.klineLow = price;
      this.klineVolume += safeQty;
    }

    this.klineUpdate([
      this.klineBucketStart,
      String(this.klineOpen),
      String(this.klineHigh),
      String(this.klineLow),
      String(this.klineClose),
      String(this.klineVolume),
    ] as unknown as KLine);
  };

  private resolveWsUrl = async (): Promise<string> => {
    const res = await proxyAxios.get<{ data?: CoinwPublicToken }>('/api/v1/proxy', {
      params: { url: COINW_PUBLIC_TOKEN },
    });

    const token = res.data?.data;
    if (!token?.token || !token?.endpoint) {
      throw new Error('CoinW WS: failed to obtain token/endpoint');
    }

    this.intervalPing = clampPingInterval(token.pingInterval);

    const endpoint = token.endpoint.replace(/\/+$/, '');
    const protocol = token.protocol || 'websocket';

    return `${endpoint}/socket.io/?token=${token.token}&EIO=3&transport=${protocol}`;
  };

  intervalPing = DEFAULT_PING_INTERVAL_MS;

  connectWebSocket = ({ interval }: { interval: number }) => {
    if (this.ws) {
      this.closeWebSocket();
    }

    this.intervalMs = interval > 0 ? interval : 300_000;

    const ws = new ReconnectingWebSocket(this.resolveWsUrl, [], {
      maxRetries: 10,
      minReconnectionDelay: 1000,
    });

    this.ws = ws;

    ws.addEventListener('open', () => {
      // socket.io EVENT subscribe frames (one connection, multiple channels).
      ws.send(`42["subscribe", {"args": "${DEPTH_CHANNEL}${this.wsPair}"}]`);
      ws.send(`42["subscribe", {"args": "${TICKER_CHANNEL}${this.wsPair}"}]`);
      ws.send(`42["subscribe", {"args": "${TRADE_CHANNEL}${this.wsPair}"}]`);

      // Re-seed the orderbook snapshot so deltas apply on a fresh baseline.
      void this.fetchOrderbook();

      if (this.pingInterval) {
        clearInterval(this.pingInterval);
      }
      this.pingInterval = setInterval(() => {
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
          this.ws.send('2'); // Engine.IO ping
        }
      }, this.intervalPing);
    });

    ws.addEventListener('message', rawEvent => {
      try {
        const raw = typeof rawEvent.data === 'string' ? rawEvent.data : '';
        if (!raw) {
          return;
        }

        // Engine.IO heartbeat.
        if (raw === '2') {
          if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            this.ws.send('3');
          }
          return;
        }
        if (raw === '3') {
          return;
        }

        // Only socket.io EVENT frames carry data. They are prefixed with `42`
        // (Engine.IO MESSAGE + socket.io EVENT), optionally followed by an ack
        // id. The Engine.IO open frame (`0{...,"upgrades":["websocket"],...}`)
        // and connect ack (`40`) also contain `[`, so guard on the `42` prefix
        // to avoid mis-parsing them.
        if (!raw.startsWith('42')) {
          return;
        }

        const bracket = raw.indexOf('[');
        if (bracket === -1) {
          return;
        }

        const parsed = JSON.parse(raw.slice(bracket));
        if (!Array.isArray(parsed) || parsed.length < 2) {
          return;
        }

        const [eventName, envelope] = parsed as [unknown, { channel?: string; data?: unknown }];
        if (typeof eventName !== 'string' || typeof envelope !== 'object' || envelope === null) {
          return;
        }

        const channel = String(envelope.channel ?? '');

        if (channel.startsWith(DEPTH_CHANNEL)) {
          const depth = parseEnvelopeData<CoinwDepthData>(envelope);
          if (!depth) {
            return;
          }
          this.applyLevels(depth.bids, this.bids);
          this.applyLevels(depth.asks, this.asks);
          this.emitOrderbook();
          return;
        }

        if (channel.startsWith(TICKER_CHANNEL)) {
          const ticker = parseEnvelopeData<CoinwTickerData>(envelope);
          if (!ticker) {
            return;
          }
          const last = ticker.last ?? '0';
          const change =
            ticker.changeRate !== undefined && ticker.changeRate !== ''
              ? normalizeNumber(Number(ticker.changeRate) * 100)
              : (ticker.changePrice ?? '0');

          this.onTickerUpdate({
            baseVolume: ticker.vol ?? '0',
            quoteVolume: ticker.volValue ?? '0',
            change,
            close: last,
            last,
            high: ticker.high ?? '0',
            low: ticker.low ?? '0',
          });
          return;
        }

        if (channel.startsWith(TRADE_CHANNEL)) {
          const tradesRaw = parseEnvelopeData<CoinwTradeData[] | CoinwTradeData>(envelope);
          // CoinW may deliver the trade payload as an array or a single object.
          const trades = Array.isArray(tradesRaw) ? tradesRaw : tradesRaw ? [tradesRaw] : [];
          if (!trades.length) {
            return;
          }
          const ordered = trades
            .filter(Boolean)
            .slice()
            .sort((a, b) => toMs(a.time) - toMs(b.time));

          for (const trade of ordered) {
            this.onTradeUpdate({
              price: trade.price ?? '0',
              quantity: trade.size ?? '0',
              timestamp: String(toMs(trade.time)),
              side: String(trade.side).toUpperCase() === 'SELL' ? 'sell' : 'buy',
            });
            this.aggregateTradeIntoKline(trade);
          }
          return;
        }
      } catch (error) {
        console.log('CoinW socket error', error);
      }
    });
  };

  closeWebSocket = () => {
    if (this.ws) {
      this.ws.close();
      this.ws = undefined;
    }
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = undefined;
    }
  };

  switchOrderbookTick = ({ tick }: { tick: number }) => {
    this.selectedPrecision = tick;
    this.emitOrderbook();
  };

  getOrderbookTicks = (prec: number) =>
    Array.from({ length: prec })
      .fill(0)
      .map((_, idx) => idx + 1)
      .map(el => ({ value: normalizeNumber(1 / 10 ** el), tick: el, id: el }))
      .slice(-3);

  fetch = async () => {
    await this.fetchPrecision();
    await this.fetchOrderbook();
  };

  static init = async (args: IInitArgs) => {
    const instance = new CoinwStrategy(args);

    await instance.fetch();

    return instance;
  };
}
